import {
  type QueryClient,
  type QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useCallback, useEffect, useRef } from "react";
import { useApi } from "@/core/api";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import {
  isPayrollStatementDeliveryInProgress,
  isSmsDeliveryInFlight,
} from "./delivery-status-rules";
import {
  DEFAULT_MAX_POLL_INTERVAL_MS,
  DEFAULT_POLL_TIMEOUT_MS,
  pollPayrollStatementStatus,
  type PollingResult,
} from "./polling";
import type {
  PayrollStatementNotificationChannel,
  PayrollStatementDeliveryStatusResponse,
  PayrollStatementNotificationSendResponse,
  PayrollStatementNotificationState,
  PayrollStatementPackApprovalsResponse,
  PayrollStatementProjectionBuild,
} from "./types";
import {
  getDisplayedProjectionBuild,
  mergePayrollStatementDeliveryStatuses,
} from "./utils";

export const payrollStatementNotificationsPath = (
  businessId: string,
  periodStart: string,
) =>
  `/businesses/${businessId}/pay_periods/${periodStart}/payroll_statement_notifications`;

export const payrollStatementNotificationsKey = (
  businessId: string | null,
  periodStart: string | null,
) => ["payroll-statement-notifications", businessId, periodStart] as const;

export const payrollStatementPackApprovalsKey = (
  businessId: string | null,
  periodStart: string | null,
) => ["payroll-statement-pack-approvals", businessId, periodStart] as const;

/**
 * The technicians' approvals for the period, on their own so they can stay fresh.
 *
 * Approvals can change outside this browser. Poll the lightweight endpoint so
 * the approval column stays current without loading statement projections on
 * every approval check.
 *
 * Refreshes on focus and on a slow interval. Cheap enough for both: the endpoint reads
 * the approval rows and one fingerprint pass, and queues no projection work.
 */
export const useGetPayrollStatementPackApprovals = (
  businessId: string | null,
  periodStart: string | null,
  enabled: boolean,
) => {
  const api = useApi();
  return useQuery({
    queryKey: payrollStatementPackApprovalsKey(businessId, periodStart),
    queryFn: ({ signal }: QueryFunctionContext) => {
      if (!businessId || !periodStart) {
        throw new Error("Pay period is required");
      }

      return api
        .get(
          `${payrollStatementNotificationsPath(businessId, periodStart)}/pack_approvals`,
          { signal },
        )
        .then((res) => res.data as PayrollStatementPackApprovalsResponse);
    },
    enabled: enabled && !!businessId && !!periodStart,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    // Off the rollout this returns an empty list rather than failing, so a retry only
    // costs requests.
    retry: false,
  });
};

export const payrollStatementProjectionBuildKeyPrefix = (
  businessId: string | null,
  periodStart: string | null,
) => ["payroll-statement-projection-build", businessId, periodStart] as const;

export const payrollStatementProjectionBuildKey = (
  businessId: string | null,
  periodStart: string | null,
  generationKey: string | null,
) =>
  [
    ...payrollStatementProjectionBuildKeyPrefix(businessId, periodStart),
    generationKey,
  ] as const;

export const payrollStatementDeliveryStatusKey = (
  businessId: string | null,
  periodStart: string | null,
  batchId: string | null,
) =>
  [
    "payroll-statement-delivery-status",
    businessId,
    periodStart,
    batchId,
  ] as const;

export const payrollStatementNotificationsQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  periodStart: string | null,
) => ({
  queryKey: payrollStatementNotificationsKey(businessId, periodStart),
  queryFn: ({ signal }: QueryFunctionContext) => {
    if (!businessId || !periodStart) {
      throw new Error("Pay period is required");
    }

    return api
      .get(payrollStatementNotificationsPath(businessId, periodStart), {
        signal,
      })
      .then((res) => res.data as PayrollStatementNotificationState);
  },
  staleTime: 0,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  refetchInterval: 30_000,
  retry: false,
});

export const useGetPayrollStatementNotifications = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();

  return useQuery({
    ...payrollStatementNotificationsQueryOptions(api, businessId, periodStart),
    enabled: !!businessId && !!periodStart,
  });
};

export const payrollStatementProjectionBuildQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  periodStart: string | null,
  initial: PayrollStatementProjectionBuild | null | undefined,
) => ({
  queryKey: payrollStatementProjectionBuildKey(
    businessId,
    periodStart,
    initial?.generation_key ?? null,
  ),
  queryFn: ({ signal }: QueryFunctionContext) => {
    if (!businessId || !periodStart || !initial) {
      throw new Error("Projection build is required");
    }

    const path = `${payrollStatementNotificationsPath(
      businessId,
      periodStart,
    )}/projection_build`;
    return pollPayrollStatementStatus({
      initial,
      isPending: (build) => build.status === "pending",
      request: (requestSignal) =>
        api
          .get(path, { signal: requestSignal })
          .then(
            (res) =>
              res.data.projection_build as PayrollStatementProjectionBuild,
          ),
      signal,
    });
  },
  staleTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
});

export const usePayrollStatementProjectionBuild = (
  businessId: string | null,
  periodStart: string | null,
  initial: PayrollStatementProjectionBuild | null | undefined,
) => {
  const api = useApi();

  const query = useQuery({
    ...payrollStatementProjectionBuildQueryOptions(
      api,
      businessId,
      periodStart,
      initial,
    ),
    enabled:
      !!businessId &&
      !!periodStart &&
      !!initial &&
      initial.status === "pending",
  });

  return { ...query, projectionBuild: query.data ?? initial ?? null };
};

export const usePayrollStatementNotifications = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const fetchedProjectionGenerationRef = useRef<string | null>(null);
  useEffect(() => {
    fetchedProjectionGenerationRef.current = null;
  }, [businessId, periodStart]);

  const notificationsQuery = useGetPayrollStatementNotifications(
    businessId,
    periodStart,
  );
  const projectionQuery = usePayrollStatementProjectionBuild(
    businessId,
    periodStart,
    notificationsQuery.data?.projection_build,
  );
  const projectionBuild: PollingResult<PayrollStatementProjectionBuild> | null =
    projectionQuery.projectionBuild;
  const projectionReady =
    notificationsQuery.data?.projection_build?.status === "ready";
  const refetchNotifications = notificationsQuery.refetch;
  const hydrateReadyProjection = useCallback(
    async (generationKey: string | null) => {
      const result = await refetchNotifications();
      if (result.data?.projection_build?.status === "ready") {
        fetchedProjectionGenerationRef.current = generationKey;
      }
      return result;
    },
    [refetchNotifications],
  );

  useEffect(() => {
    const generationKey = projectionBuild?.generation_key;
    if (
      projectionBuild?.status !== "ready" ||
      projectionReady ||
      !generationKey ||
      fetchedProjectionGenerationRef.current === generationKey
    ) {
      return;
    }

    void hydrateReadyProjection(generationKey);
  }, [hydrateReadyProjection, projectionBuild, projectionReady]);

  const canRetryProjectionHydration =
    projectionBuild?.status === "ready" &&
    !projectionReady &&
    !!notificationsQuery.error;
  const refetchProjectionBuild = () => {
    if (projectionBuild?.status === "ready" && !projectionReady) {
      return hydrateReadyProjection(projectionBuild.generation_key);
    }

    return projectionQuery.refetch();
  };

  return {
    ...currentPayrollResult(notificationsQuery),
    projectionBuild: getDisplayedProjectionBuild({
      indexBuild: notificationsQuery.data?.projection_build,
      polledBuild: projectionBuild,
    }),
    isProjectionRefreshing:
      projectionQuery.isFetching || notificationsQuery.isFetching,
    canRetryProjectionHydration,
    refetchProjectionBuild,
  };
};

export const useRetryPayrollStatementProjectionBuild = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    retryPayrollStatementProjectionBuildMutationOptions(
      api,
      queryClient,
      businessId,
      periodStart,
    ),
  );
};

export const retryPayrollStatementProjectionBuildMutationOptions = (
  api: AxiosInstance,
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
) => ({
  mutationFn: () => {
    if (!businessId || !periodStart) {
      throw new Error("Pay period is required");
    }

    return api
      .post(
        `${payrollStatementNotificationsPath(
          businessId,
          periodStart,
        )}/projection_build/retry`,
      )
      .then(
        (res) => res.data.projection_build as PayrollStatementProjectionBuild,
      );
  },
  onSuccess: async (projectionBuild: PayrollStatementProjectionBuild) => {
    queryClient.removeQueries({
      queryKey: payrollStatementProjectionBuildKeyPrefix(
        businessId,
        periodStart,
      ),
    });

    if (projectionBuild.status === "ready") {
      await queryClient.fetchQuery({
        ...payrollStatementNotificationsQueryOptions(
          api,
          businessId,
          periodStart,
        ),
        staleTime: 0,
      });
      return;
    }

    queryClient.setQueriesData<PayrollStatementNotificationState>(
      { queryKey: payrollStatementNotificationsKey(businessId, periodStart) },
      (state) =>
        state ? { ...state, projection_build: projectionBuild } : state,
    );
  },
});

export const useSendPayrollStatementNotifications = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      technicianIds,
      channels,
    }: {
      technicianIds: string[];
      /** Omitted when there is no choice to make; the API then uses every channel. */
      channels?: PayrollStatementNotificationChannel[] | null;
    }) => {
      if (!businessId || !periodStart) {
        throw new Error("Pay period is required");
      }

      return api
        .post(
          `${payrollStatementNotificationsPath(businessId, periodStart)}/send`,
          {
            technician_ids: technicianIds,
            ...(channels ? { channels } : {}),
          },
        )
        .then((res) => res.data as PayrollStatementNotificationSendResponse);
    },
    onSuccess: (response) => {
      queryClient.setQueriesData<PayrollStatementNotificationState>(
        { queryKey: payrollStatementNotificationsKey(businessId, periodStart) },
        (state) =>
          mergePayrollStatementDeliveryStatuses(state, response.deliveries),
      );
    },
  });
};

export const payrollStatementDeliveryStatusQueryOptions = (
  api: AxiosInstance,
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
  seed: PayrollStatementNotificationSendResponse | null,
) => {
  const batchId = seed?.batch.id ?? null;
  let seeded = false;

  return {
    queryKey: payrollStatementDeliveryStatusKey(
      businessId,
      periodStart,
      batchId,
    ),
    queryFn: async ({ signal }: QueryFunctionContext) => {
      if (!businessId || !periodStart || !seed) {
        throw new Error("Delivery batch is required");
      }

      const initial: PayrollStatementDeliveryStatusResponse = {
        batch: seed.batch,
        deliveries: seed.deliveries,
        poll_after_ms: 2_000,
        max_poll_interval_ms: DEFAULT_MAX_POLL_INTERVAL_MS,
        poll_timeout_ms: DEFAULT_POLL_TIMEOUT_MS,
      };
      const merge = (response: PayrollStatementDeliveryStatusResponse) => {
        queryClient.setQueriesData<PayrollStatementNotificationState>(
          {
            queryKey: payrollStatementNotificationsKey(businessId, periodStart),
          },
          (state) =>
            mergePayrollStatementDeliveryStatuses(state, response.deliveries),
        );
      };
      if (!seeded) {
        seeded = true;
        merge(initial);
      }

      const result = await pollPayrollStatementStatus({
        initial,
        isPending: (response) =>
          response.deliveries.some(
            (delivery) =>
              isPayrollStatementDeliveryInProgress(delivery.status) ||
              isSmsDeliveryInFlight(delivery.sms),
          ),
        request: (requestSignal) =>
          api
            .get(
              `${payrollStatementNotificationsPath(
                businessId,
                periodStart,
              )}/delivery_status`,
              { params: { batch_id: batchId }, signal: requestSignal },
            )
            .then((res) => res.data as PayrollStatementDeliveryStatusResponse),
        signal,
        onUpdate: merge,
      });

      if (!result.polling_timed_out) {
        await queryClient.fetchQuery({
          ...payrollStatementNotificationsQueryOptions(
            api,
            businessId,
            periodStart,
          ),
          staleTime: 0,
        });
      }
      return result;
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
  };
};

export const usePayrollStatementDeliveryPolling = (
  businessId: string | null,
  periodStart: string | null,
  seed: PayrollStatementNotificationSendResponse | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    ...payrollStatementDeliveryStatusQueryOptions(
      api,
      queryClient,
      businessId,
      periodStart,
      seed,
    ),
    enabled: !!businessId && !!periodStart && !!seed,
  });
};

// Autosaves (upserts) a manager's draft note for one technician's next send.
export const useSavePayrollStatementNote = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { technicianId: string; note: string }) => {
      if (!businessId || !periodStart) {
        throw new Error("Pay period is required");
      }

      return api
        .put(
          `${payrollStatementNotificationsPath(businessId, periodStart)}/note`,
          { technician_id: input.technicianId, note: input.note },
        )
        .then(
          (res) => res.data as { technician_id: string; note: string | null },
        );
    },
    onSuccess: ({ technician_id, note }) => {
      queryClient.setQueriesData<PayrollStatementNotificationState>(
        { queryKey: payrollStatementNotificationsKey(businessId, periodStart) },
        (state) =>
          state
            ? {
                ...state,
                recipients: state.recipients.map((recipient) =>
                  recipient.technician_id === technician_id
                    ? { ...recipient, note }
                    : recipient,
                ),
              }
            : state,
      );
    },
  });
};
